import React from 'react';
import { CheckCircle, MessageSquare, Users, Clock } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { companyInfo } from '../data/mock';

const CustomerServicePolicyPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <section className="pt-32 pb-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="heading-1 mb-4">Customer Service Policy</h1>
            <p className="text-[#B5D43B] font-medium italic mb-8">Applies from first enquiry through to project handover</p>
            
            <div className="prose prose-gray max-w-none space-y-8">
              <p className="body-large">
                At Supa Energy Hub, we are committed to delivering a professional, transparent, and supportive customer experience from your first enquiry through to final <strong>handover</strong>.
              </p>
              
              <div className="bg-[#B5D43B]/10 rounded-xl p-6 border border-[#B5D43B]/20">
                <p className="text-[#3D5A47] font-medium">
                  <strong>"Handover"</strong> refers to the point at which we have fully delivered the product or service you purchased — including installation, commissioning, final documentation, and grid connection (if applicable). At this point, all agreed deliverables under the sale have been completed.
                </p>
              </div>

              {/* Our Commitments */}
              <section>
                <h2 className="heading-3 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-[#B5D43B]" />
                  Our Commitments to You
                </h2>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#B5D43B] mt-2 flex-shrink-0"></div>
                    <div>
                      <strong className="text-[#3D5A47]">Clear Communication</strong>
                      <span className="text-gray-600"> – We provide transparent advice on products, pricing, processes, rebates, and timelines.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#B5D43B] mt-2 flex-shrink-0"></div>
                    <div>
                      <strong className="text-[#3D5A47]">Reliable Timeframes</strong>
                      <span className="text-gray-600"> – We respond to all customer enquiries and service-related matters within <strong>1–2 business days</strong>.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#B5D43B] mt-2 flex-shrink-0"></div>
                    <div>
                      <strong className="text-[#3D5A47]">Professionalism</strong>
                      <span className="text-gray-600"> – Our team is courteous, respectful, and focused on providing excellent service at every stage.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#B5D43B] mt-2 flex-shrink-0"></div>
                    <div>
                      <strong className="text-[#3D5A47]">Qualified Personnel</strong>
                      <span className="text-gray-600"> – All installations and technical work are carried out by licensed, accredited professionals.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#B5D43B] mt-2 flex-shrink-0"></div>
                    <div>
                      <strong className="text-[#3D5A47]">Complete Delivery</strong>
                      <span className="text-gray-600"> – We ensure your system is installed, commissioned, documented, and handed over in full compliance with all requirements.</span>
                    </div>
                  </li>
                </ul>
              </section>

              {/* Consistent Experience */}
              <section>
                <h2 className="heading-3 mb-4 flex items-center gap-2">
                  <Users className="w-6 h-6 text-[#B5D43B]" />
                  A Consistent Experience Across Teams
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  You may engage with different Supa Energy Hub teams throughout your journey:
                </p>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#3D5A47] mt-2 flex-shrink-0"></div>
                    <span>Our <strong>Sales Consultants</strong> provide personalised support, helping you make informed decisions and guiding you through the onboarding process.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#3D5A47] mt-2 flex-shrink-0"></div>
                    <span>Our <strong>Operations Team</strong> manages your installation, approval processes, scheduling, and technical compliance through to handover.</span>
                  </li>
                </ul>
                <p className="text-gray-600 leading-relaxed mt-4">
                  While the nature of their roles differs, our standard of service remains the same: <strong>prompt, professional, and solution-focused</strong>. Each team is committed to ensuring a smooth and positive experience.
                </p>
              </section>

              {/* Official Channels */}
              <section>
                <h2 className="heading-3 mb-4 flex items-center gap-2">
                  <MessageSquare className="w-6 h-6 text-[#B5D43B]" />
                  Official Customer Service Channels
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  To ensure your enquiry is received, tracked, and responded to efficiently, all customer service communications must be made through our <strong>official channels</strong>:
                </p>
                <div className="bg-white rounded-xl p-6 border border-black/5 space-y-3">
                  <p className="flex items-center gap-3 text-[#3D5A47] font-medium">
                    <span className="text-xl">📧</span>
                    hello@supaenergyhub.com.au
                  </p>
                  <p className="flex items-center gap-3 text-[#3D5A47] font-medium">
                    <span className="text-xl">📞</span>
                    {companyInfo.phone}
                  </p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 mt-4">
                  <p className="text-amber-800 text-sm">
                    <strong>Please do not contact individual team members</strong> (including Sales Consultants, Operations staff, Installers, or others) via personal mobile numbers or private email addresses. While our team members may go above and beyond during the sales process, the wider support process relies on centralised systems to ensure timely and accurate handling of all enquiries.
                  </p>
                </div>
                <p className="text-gray-600 leading-relaxed mt-4">Using official channels ensures:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600 mt-2">
                  <li>Requests are properly logged and prioritised</li>
                  <li>You receive consistent and fair service</li>
                  <li>Your enquiry reaches the right team without delays</li>
                </ul>
              </section>

              {/* What You Can Expect */}
              <section>
                <h2 className="heading-3 mb-4 flex items-center gap-2">
                  <Clock className="w-6 h-6 text-[#B5D43B]" />
                  What You Can Expect
                </h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  <li>Honest and proactive communication</li>
                  <li>Timely responses and updates on your project</li>
                  <li>A structured, well-managed process from sale to handover</li>
                  <li>A comprehensive handover including documentation, system setup, and compliance completion</li>
                </ul>
              </section>

              {/* What We Ask */}
              <section>
                <h2 className="heading-3 mb-4">🙏 What We Ask in Return</h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  <li><strong>Patience</strong> in cases where delays are caused by third-party entities (e.g., DNSPs, government rebates, electricity retailers)</li>
                  <li><strong>Use of official communication channels</strong> for all service or support enquiries</li>
                  <li><strong>Respectful communication</strong> with our team at all times</li>
                  <li><strong>Understanding</strong> that while some individuals may provide extraordinary support, this should be seen as a bonus, not the baseline expectation</li>
                </ul>
              </section>

              {/* After Handover */}
              <section className="pt-8 border-t border-black/10">
                <p className="text-gray-600 leading-relaxed">
                  After your handover is complete, our support continues under our separate <a href="/customer-care-support-policy" className="text-[#3D5A47] font-medium hover:text-[#B5D43B] underline">Customer Care & Support Policy</a>, which covers warranty queries, technical troubleshooting, and post-installation assistance.
                </p>
                <div className="bg-white rounded-xl p-6 border border-black/5 mt-6 space-y-2">
                  <p className="flex items-center gap-3 text-[#3D5A47]">
                    <span>📧</span>
                    <strong>hello@supaenergyhub.com.au</strong>
                  </p>
                  <p className="flex items-center gap-3 text-[#3D5A47]">
                    <span>📞</span>
                    <strong>{companyInfo.phone}</strong>
                  </p>
                  <p className="flex items-center gap-3 text-[#3D5A47]">
                    <span>🌐</span>
                    <strong>supaenergyhub.com.au</strong>
                  </p>
                </div>
              </section>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CustomerServicePolicyPage;
